import { createSelector } from 'reselect';
import {
  filter,
  find,
  flow,
  fromPairs,
  get,
  getOr,
  has,
  head,
  identity,
  isNil,
  isObject,
  join,
  map,
  omit,
  reject,
  set,
  split,
  toUpper,
  isEqual,
  uniq,
} from 'lodash/fp';

import getMapper, { cleanEmptyValues, mapPath } from 'helpers/mapper';
import { inputBoxesFromArray } from 'components/forms/InputBoxes';
import { byId as airbagsById } from 'helpers/fieldOptions/car/airbag';
import { byId as additionalFeaturesById } from 'helpers/fieldOptions/car/additional';
import { byId as comfortById } from 'helpers/fieldOptions/car/comfort';
import { byId as extrasById } from 'helpers/fieldOptions/car/extra';
import { byId as interiorsById } from 'helpers/fieldOptions/car/interior';
import { byId as lightsById } from 'helpers/fieldOptions/car/light';
import { byId as multimediaById } from 'helpers/fieldOptions/car/multimedia';
import { byId as seatComfortById } from 'helpers/fieldOptions/car/seatComfort';
import { byId as securityById } from 'helpers/fieldOptions/car/security';
import { byId as parkingSensorsById } from 'helpers/fieldOptions/car/parkingSensor';
import { valuesById as bodyTypeValuesById } from 'helpers/fieldOptions/car/bodyType';
import { encodeName } from 'helpers/fieldOptions';
import { VALUE_10 as DEFAULT_PAGE_SIZE } from 'helpers/fieldOptions/ad/pageSize';
import { GEOLOCATION_DISTANCE_ASC } from 'helpers/fieldOptions/ad/sort';
import { options as adSellerTypeOptions } from 'helpers/fieldOptions/ad/sellerType';
import { byId as upholsteryById } from 'helpers/fieldOptions/car/upholstery';
import { values as priceTypeValues } from 'helpers/fieldOptions/ad/priceType';
import {
  ADDITIONAL_NAME,
  AIRBAG_NAME,
  BODY_TYPE_NAME,
  COLOR_NAME,
  COMFORT_NAME,
  CONSUMPTION_RANGE_NAME,
  DOOR_RANGE_NAME,
  EMISSION_STICKER_NAME,
  ENV_STANDARD_NAME,
  EXCLUSIVE_NAME,
  EXTRAS_NAME,
  FIELDS_NAME,
  FUEL_TYPE_NAME,
  GEOLOCATION_NAME,
  HAS_IMAGES_NAME,
  INTERIOR_NAME,
  LIGHT_NAME,
  MAKE_NAME,
  METALLIC_NAME,
  MILEAGE_RANGE_NAME,
  MODEL_NAME,
  MONTHLY_PRICE_RANGE_NAME,
  MULTIMEDIA_NAME,
  NOT_DAMAGED_NAME,
  PAGE_NAME,
  PAGE_SIZE_NAME,
  PARKING_SENSORS_NAME,
  POWER_RANGE_NAME,
  PREV_OWNER_NAME,
  PRICE_RANGE_NAME,
  PRICE_TYPE_NAME,
  SEAT_COMFORT_NAME,
  SEAT_RANGE_NAME,
  SECURITY_NAME,
  SELLER_ID_NAME,
  SELLER_TYPE_NAME,
  SERVICE_HISTORY_NAME,
  SMOKER_NAME,
  SORT_NAME,
  SUB_TYPE_NAME,
  TRANSMISSION_NAME,
  UPHOLSTERY_NAME,
  VEHICLE_NAME,
  YEAR_RANGE_NAME,
} from '../../constants';
import scaffolding from '../formScaffolding';

const toString = (value) => {
  if (isNil(value)) {
    return;
  }

  return String(value);
};

const getBodyType = flow(
  get(['bodyType']),
  map((id) => encodeName(bodyTypeValuesById[id])),
  uniq,
  map((value) => [value, true]),
  fromPairs,
);

const getCheckboxList = (path) =>
  flow(get(path), inputBoxesFromArray(encodeName));

const getMetallic = flow(get(['outerColorType']), (value) => {
  if (isNil(value)) {
    return;
  }

  if (value === 'true') {
    return {
      _true: true,
    };
  }
});

const moveKey =
  (keyFrom, keyTo, toType = identity) =>
  (object) =>
    has([keyFrom], object)
      ? set([keyTo], toType(get([keyFrom], object)), omit([keyFrom], object))
      : object;

const moveRange = (value) =>
  isObject(value)
    ? flow(
        moveKey('from', 'min', toString),
        moveKey('to', 'max', toString),
      )(value)
    : {
        min: toString(value),
        max: toString(value),
      };

const getRange = (path) =>
  flow(get(path), (value) => {
    if (isNil(value)) {
      return;
    }

    return moveRange(value);
  });

const getSort = flow(
  get(['sort']),
  reject({ field: 'sellerType' }),
  head,
  (object) => {
    if (!object) {
      return;
    }

    const { field, direction, distance } = object;

    if (distance) {
      return flow(split('.'), head, (field) => [field, direction])(
        GEOLOCATION_DISTANCE_ASC,
      );
    }

    return [field, direction];
  },
  join('.'),
);

const getPage = createSelector(
  get(['from']),
  get(['size']),
  flow((from, size) => {
    if (isNil(from) || isNil(size)) {
      return;
    }

    return Math.floor(from / size) + 1;
  }, toString),
);

const getGeoLocation = createSelector(
  getOr({}, ['location', 'locations', '0']),
  ({ lon: lng, ...rest }) => ({
    lng,
    ...rest,
  }),
);

const getVehicle = (values) => {
  const make = get(['makes', 0, 'make'], values);
  const model = get(['makes', 0, 'models', 0], values);
  const subType = get(
    ['makes', 0, 'modelsWithSubTypes', 0, 'subTypes', 0],
    values,
  );

  let vehicle;

  if (make && model && subType) {
    vehicle = {
      [MAKE_NAME]: make,
      [MODEL_NAME]: toUpper(model),
      [SUB_TYPE_NAME]: toUpper(subType),
    };
  } else if (make && model) {
    vehicle = {
      [MAKE_NAME]: make,
      [MODEL_NAME]: toUpper(model),
    };
  } else if (make) {
    vehicle = {
      [MAKE_NAME]: make,
    };
  }

  if (!vehicle) {
    return;
  }

  return [vehicle];
};

const getEquipmentGroups = get(['equipmentGroups']);

const getAirbag = flow(
  getEquipmentGroups,
  find((value) => airbagsById[value]),
  toString,
);

const getEquipmentCheckboxList = (ids) =>
  flow(
    getEquipmentGroups,
    filter((value) => ids[value]),
    inputBoxesFromArray(encodeName),
  );

const getProvider = createSelector(
  get([EXCLUSIVE_NAME]),
  get([SELLER_TYPE_NAME]),
  (exclusive, sellerType) => {
    if (exclusive) {
      const value = flow(
        find((option) => isEqual(option.provider, EXCLUSIVE_NAME)),
        get(['value']),
      )(adSellerTypeOptions);

      return value;
    }

    return sellerType;
  },
);

const getNotDamaged = flow(get(['damageBool']), (value) => {
  if (value === false) {
    return true;
  }
});

const getPriceType = (values) => {
  const { price, monthlyPayment } = values;

  if (price && monthlyPayment) {
    return priceTypeValues.BUY_PRICE;
  }

  if (monthlyPayment) {
    return priceTypeValues.MONTHLY_PRICE;
  }

  return priceTypeValues.BUY_PRICE;
};

export default getMapper(
  [
    mapPath([AIRBAG_NAME], getAirbag),
    mapPath(
      [ADDITIONAL_NAME],
      getEquipmentCheckboxList(additionalFeaturesById),
    ),
    mapPath([BODY_TYPE_NAME], getBodyType),
    mapPath([COLOR_NAME], getCheckboxList(['outerColor'])),
    mapPath([COMFORT_NAME], getEquipmentCheckboxList(comfortById)),
    mapPath([CONSUMPTION_RANGE_NAME], getRange(['fuelConsumptionCombined'])),
    mapPath([GEOLOCATION_NAME], getGeoLocation),
    mapPath([DOOR_RANGE_NAME], getRange(['doorCount'])),
    mapPath([EMISSION_STICKER_NAME], get(['emissionSticker'])),
    mapPath([ENV_STANDARD_NAME], get(['environmentalStandard'])),
    mapPath([EXTRAS_NAME], getEquipmentCheckboxList(extrasById)),
    mapPath([FIELDS_NAME], get(['fields'])),
    mapPath([HAS_IMAGES_NAME], get([HAS_IMAGES_NAME])),
    mapPath([FUEL_TYPE_NAME], getCheckboxList(['fuelType'])),
    mapPath([INTERIOR_NAME], getEquipmentCheckboxList(interiorsById)),
    mapPath([LIGHT_NAME], getEquipmentCheckboxList(lightsById)),
    mapPath([METALLIC_NAME], getMetallic),
    mapPath([MONTHLY_PRICE_RANGE_NAME], getRange(['monthlyPayment'])),
    mapPath([MILEAGE_RANGE_NAME], getRange(['mileage'])),
    mapPath([MULTIMEDIA_NAME], getEquipmentCheckboxList(multimediaById)),
    mapPath([PAGE_NAME], getPage),
    mapPath([PAGE_SIZE_NAME], getOr(DEFAULT_PAGE_SIZE, ['size'])),
    mapPath(
      [PARKING_SENSORS_NAME],
      getEquipmentCheckboxList(parkingSensorsById),
    ),
    mapPath([POWER_RANGE_NAME], getRange(['horsePower'])),
    mapPath([PREV_OWNER_NAME], getRange(['carPreownerCount'])),
    mapPath([PRICE_RANGE_NAME], getRange(['price'])),
    mapPath([PRICE_TYPE_NAME], getPriceType),
    mapPath([SEAT_RANGE_NAME], getRange(['seatCount'])),
    mapPath([SEAT_COMFORT_NAME], getEquipmentCheckboxList(seatComfortById)),
    mapPath([SECURITY_NAME], getEquipmentCheckboxList(securityById)),
    mapPath([SELLER_ID_NAME], get(['sellerId'])),
    mapPath([NOT_DAMAGED_NAME], getNotDamaged),
    mapPath([SMOKER_NAME], get(['smoker'])),
    mapPath([SELLER_TYPE_NAME], getProvider),
    mapPath([SERVICE_HISTORY_NAME], getCheckboxList(['serviceBook'])),
    mapPath([SORT_NAME], getSort),
    mapPath([TRANSMISSION_NAME], getCheckboxList(['gearType'])),
    mapPath([UPHOLSTERY_NAME], getEquipmentCheckboxList(upholsteryById)),
    mapPath([VEHICLE_NAME], getVehicle),
    mapPath([YEAR_RANGE_NAME], getRange(['registrationDate'])),
    cleanEmptyValues,
  ],
  scaffolding,
);
